import { Node, mergeAttributes } from "@tiptap/core"

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    details: {
      insertCollapsible: () => ReturnType
      removeCollapsible: () => ReturnType
    }
  }
}

export const DetailsSummary = Node.create({
  name: "detailsSummary",
  content: "inline*",
  defining: true,
  selectable: false,

  parseHTML() {
    return [{ tag: "summary" }]
  },

  renderHTML({ HTMLAttributes }) {
    return ["summary", mergeAttributes(HTMLAttributes), 0]
  },
})

export const Details = Node.create({
  name: "details",
  group: "block",
  content: "detailsSummary block+",
  defining: true,

  addAttributes() {
    return {
      open: {
        default: true,
        parseHTML: (element) => element.hasAttribute("open"),
        renderHTML: (attributes) => {
          if (attributes.open) {
            return { open: "" }
          }
          return {}
        },
      },
    }
  },

  parseHTML() {
    return [{ tag: "details" }]
  },

  renderHTML({ HTMLAttributes }) {
    return ["details", mergeAttributes(HTMLAttributes), 0]
  },

  addNodeView() {
    return ({ node: initialNode, editor, getPos }) => {
      const dom = document.createElement("div")
      const contentDOM = document.createElement("div")

      dom.classList.add("collapsible-block")

      const checkbox = document.createElement("input")
      checkbox.type = "checkbox"
      checkbox.checked = !!initialNode.attrs.open
      checkbox.className = "collapsible-toggle"
      checkbox.contentEditable = "false"

      contentDOM.className = "collapsible-content"

      dom.appendChild(checkbox)
      dom.appendChild(contentDOM)

      // Sync checkbox toggle back to the TipTap node attribute
      checkbox.addEventListener("change", () => {
        const pos = typeof getPos === "function" ? getPos() : null
        if (pos != null) {
          editor.view.dispatch(
            editor.view.state.tr.setNodeMarkup(pos, undefined, {
              ...initialNode.attrs,
              open: checkbox.checked,
            })
          )
        }
      })

      return {
        dom,
        contentDOM,
        update(updatedNode: any) {
          if (updatedNode.type !== initialNode.type) return false
          checkbox.checked = !!updatedNode.attrs.open
          return true
        },
      }
    }
  },

  addCommands() {
    return {
      insertCollapsible:
        () =>
        ({ chain }) => {
          return chain()
            .insertContent({
              type: "details",
              attrs: { open: true },
              content: [
                {
                  type: "detailsSummary",
                  content: [{ type: "text", text: "Click to expand" }],
                },
                {
                  type: "paragraph",
                },
              ],
            })
            .run()
        },

      removeCollapsible:
        () =>
        ({ state, chain }) => {
          const { $from } = state.selection

          for (let depth = $from.depth; depth >= 0; depth--) {
            if ($from.node(depth).type.name === "details") {
              const pos = $from.before(depth)
              const node = $from.node(depth)

              // Extract body content (everything after the summary)
              const content: any[] = []
              node.forEach((child, _offset, index) => {
                if (index > 0) {
                  content.push(child.toJSON())
                }
              })

              return chain()
                .deleteRange({
                  from: pos,
                  to: pos + node.nodeSize,
                })
                .insertContentAt(pos, content)
                .run()
            }
          }

          return false
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      Backspace: ({ editor }) => {
        const { $from, empty } = editor.state.selection
        if (
          empty &&
          $from.parent.type.name === "detailsSummary" &&
          $from.parentOffset === 0
        ) {
          // At start of summary — don't delete the details node
          return $from.parent.textContent === ""
        }
        return false
      },
    }
  },
})
